<template>
  <div class="max-w-prose space-y-3">
    <ReturnComponent :to="{ name: 'messages-contact' }">
      Terug naar contactopties
    </ReturnComponent>
    <TextTitle>Storing of tijdelijke wijziging doorgeven</TextTitle>
    <template v-if="submitted">
      <MessageComponent>
        <div class="space-y-2">
          <p>
            We hebben je bericht ontvangen. Jouw storing of tijdelijke wijziging
            wordt binnen {{ PROCESSING_BUSINESS_DAYS }} werkdagen verwerkt, zie
            <RouterLink
              :to="{ name: 'messages-overview' }"
              class="decoration-primary hover:text-primary underline decoration-2"
              >Mijn berichten</RouterLink
            >
            voor de status van je wijziging.
          </p>
          <BaseButton plain @click="resetForm">
            Nog een wijziging doorgeven
          </BaseButton>
        </div>
      </MessageComponent>
    </template>
    <template v-else>
      <p>
        Met dit formulier kan je een storing of tijdelijke wijziging in het
        vermogen van een asset doorgeven. Dit moet minstens
        {{ PROCESSING_BUSINESS_DAYS }} volledige werkdagen (ma - vr) van tevoren
        worden doorgegeven zodat deze tijdig verwerkt kan worden.
      </p>

      <p>
        Neem bij <span class="font-medium">dringende storingen</span> direct
        telefonisch contact op (zie
        <RouterLink
          :to="{ name: 'messages-contact' }"
          class="decoration-primary hover:text-primary underline decoration-2"
        >
          contactmogelijkheden</RouterLink
        >).
      </p>
      <p>Velden met een <span class="text-danger">*</span> zijn verplicht.</p>
      <BaseCard class="space-y-4 p-4 md:p-6">
        <ValidatedFormComponent
          ref="formRef"
          novalidate
          class="space-y-6"
          @submit.prevent="onSubmit"
        >
          <ValidatedInputComponent
            v-model="formData.asset"
            :input-component="MeteringPointPickerComponent"
            :validator="validators.asset"
            required
            label="Installatie"
            helper-text="Selecteer een zonnepark, windpark of batterij"
            no-allocation-points
            :allowed-asset-types="
              Object.keys(ALLOWED_CONSTRAINT_TYPES_PER_ASSET_TYPE)
            "
            all-open
            @update:model-value="resetConstraint"
          />
          <div class="flex flex-wrap gap-4">
            <ValidatedInputComponent
              v-model="formData.datetimeStart"
              :input-component="InputField"
              :validator="validators.datetimeStart"
              required
              label="Startdatum"
              :helper-text="`Minstens ${PROCESSING_BUSINESS_DAYS} volledige werkdagen in de toekomst`"
              type="datetime-local"
              hide-clear
              class="flex-1"
              @update:model-value="deselectDisabledRecurringDays"
            />
            <ValidatedInputComponent
              v-model="formData.datetimeEnd"
              :input-component="InputField"
              :validator="validators.datetimeEnd"
              required
              label="Einddatum"
              type="datetime-local"
              hide-clear
              class="flex-1"
              @update:model-value="deselectDisabledRecurringDays"
            />
          </div>
          <InputSwitch v-model="formData.isRecurring">
            Deze wijziging is enkel actief op bepaalde dagen en/of dagdelen.
          </InputSwitch>
          <template v-if="formData.isRecurring">
            <ValidatedInputComponent
              v-model="formData.recurringDays"
              :input-component="InputCheckboxes"
              :validator="validators.recurringDays"
              required
              label="Dagen"
              :items="recurringDayItems"
              row
            />
            <ValidatedInputComponent
              ref="timeSlotRef"
              v-model="formData.recurringHours"
              :input-component="TimeSlotPickerComponent"
              :validator="validators.recurringHours"
              label="Tijdvakken"
              helper-text="Deze tijdvakken gelden voor alle geselecteerde dagen"
            />
          </template>
          <ValidatedInputComponent
            v-model="formData.constraintType"
            :input-component="InputRadio"
            :validator="validators.constraintType"
            required
            label="Soort wijziging"
            :items="constraintTypeItems"
          />
          <ValidatedInputComponent
            v-model="formData.constraintValue"
            :input-component="InputField"
            :validator="validators.constraintValue"
            required
            :label="`Gewijzigd ${currentConstraintSpec?.quantity || 'vermogen'}`"
            type="number"
            :disabled="!formData.constraintType"
          >
            <template v-if="valueAddon" #addonRight>{{ valueAddon }}</template>
          </ValidatedInputComponent>
          <MessageComponent color="finance" in-card>
            <div class="*:text-finance-700 *:font-medium">
              Het <span>{{ summary.quantity }}</span> van de installatie
              <span>{{ summary.name }}</span> wordt van
              <span>{{ summary.datetimeStart }}</span> tot
              <span> {{ summary.datetimeEnd }} </span
              >{{ summary.isRecurring && ", "
              }}{{ summary.recurringDays && "op "
              }}<span> {{ summary.recurringDays }} </span
              >{{ summary.recurringHours && " gedurende "
              }}<span>{{ summary.recurringHours }}</span
              >{{ summary.isRecurring && "," }} beperkt tot maximaal
              <span> {{ summary.value }}{{ summary.unit }}</span
              >.
            </div>
          </MessageComponent>
          <ValidatedInputComponent
            v-model="formData.message"
            :input-component="InputArea"
            label="Toelichting"
            :validator="validators.message"
            required
          />
          <BaseButton
            type="submit"
            color="primary"
            :loading="submitting"
            class="w-fit"
          >
            Verstuur
          </BaseButton>
        </ValidatedFormComponent>
      </BaseCard>
    </template>
  </div>
</template>

<script setup>
import { ref, useTemplateRef, computed } from "vue"
import { startOfToday, eachDayOfInterval, getDay } from "date-fns"
import addBusinessDays from "@/helpers/addBusinessDays.js"
import mod from "@/helpers/mod.js"
import formatListString from "@/helpers/formatListString.js"
import { formatDateTime } from "@/helpers/formatter.js"
import {
  formatWeekdaysString,
  formatHoursString,
} from "@/helpers/timeFormatting.js"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import { triggerEvent } from "@/services/plausibleService.js"
import { ASSET_TYPE } from "@/stores/repower/meteringPointStoreFactories.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useUserStore from "@/stores/userStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  PROCESSING_BUSINESS_DAYS,
  default as useContactStore,
} from "@/stores/repower/contactStore.js"
import {
  TextTitle,
  BaseButton,
  BaseCard,
  InputField,
  InputArea,
  InputSwitch,
  InputCheckboxes,
  InputRadio,
} from "@repowerednl/ui-component-library"
import ReturnComponent from "@/components/navigation/ReturnComponent.vue"
import ValidatedInputComponent from "@/components/input/ValidatedInputComponent.vue"
import ValidatedFormComponent from "@/components/input/ValidatedFormComponent.vue"
import MeteringPointPickerComponent from "@/components/input/MeteringPointPickerComponent.vue"
import TimeSlotPickerComponent from "@/components/input/TimeSlotPickerComponent.vue"
import MessageComponent from "@/components/information/MessageComponent.vue"

const FORM_CATEGORY = "disruption"
const MIN_MESSAGE_LENGTH = 15
const SUMMARY_PLACEHOLDER = "..."
const DAYS_OF_WEEK = [
  "Maandag",
  "Dinsdag",
  "Woensdag",
  "Donderdag",
  "Vrijdag",
  "Zaterdag",
  "Zondag",
]
const CONSTRAINT_TYPES = {
  MAX_POWER_IN_KW: "MAX_POWER_IN_KW",
  MAX_POWER_OUT_KW: "MAX_POWER_OUT_KW",
  MAX_POWER_IN_FACTOR: "MAX_POWER_IN_FACTOR",
  MAX_POWER_OUT_FACTOR: "MAX_POWER_OUT_FACTOR",
}
const DEFAULT_CONSTRAINT_TYPES = {
  [CONSTRAINT_TYPES.MAX_POWER_OUT_KW]: {
    label: "Beperk maximaal opwekvermogen",
    addon: "kW",
    quantity: "opwekvermogen",
    unit: " kW",
  },
  [CONSTRAINT_TYPES.MAX_POWER_OUT_FACTOR]: {
    label: "Opwekvermogen beperken tot % van maximale capaciteit",
    addon: "%",
    quantity: "opwekvermogen",
    unit: "% van de maximale capaciteit",
  },
}
const ALLOWED_CONSTRAINT_TYPES_PER_ASSET_TYPE = {
  [ASSET_TYPE.SOLAR_PARK]: DEFAULT_CONSTRAINT_TYPES,
  [ASSET_TYPE.WIND_PARK]: DEFAULT_CONSTRAINT_TYPES,
  [ASSET_TYPE.BATTERY]: {
    [CONSTRAINT_TYPES.MAX_POWER_IN_KW]: {
      label: "Beperk maximaal oplaadvermogen",
      addon: "kW",
      quantity: "oplaadvermogen",
      unit: " kW",
    },
    [CONSTRAINT_TYPES.MAX_POWER_IN_FACTOR]: {
      label: "Laadvermogen beperken tot % van maximale capaciteit",
      addon: "%",
      quantity: "oplaadvermogen",
      unit: "% van de maximale capaciteit",
    },
    [CONSTRAINT_TYPES.MAX_POWER_OUT_KW]: {
      label: "Beperk maximaal ontlaadvermogen",
      addon: "kW",
      quantity: "ontlaadvermogen",
      unit: " kW",
    },
    [CONSTRAINT_TYPES.MAX_POWER_OUT_FACTOR]: {
      label: "Ontlaadvermogen beperken tot % van maximale capaciteit",
      addon: "%",
      quantity: "ontlaadvermogen",
      unit: "% van de maximale capaciteit",
    },
  },
}

const formRef = useTemplateRef("formRef")
const timeSlotRef = useTemplateRef("timeSlotRef")
const formData = ref({
  asset: null,
  datetimeStart: null,
  datetimeEnd: null,
  isRecurring: false,
  recurringDays: [],
  recurringHours: [],
  constraintType: null,
  constraintValue: null,
  message: "",
})
const submitting = ref(false)
const submitted = ref(false)
const notificationStore = useNotificationStore()

/**
 * Available recurring day items based on the selected start and end dates.
 */
const recurringDayItems = computed(() => {
  let days
  if (formData.value.datetimeStart && formData.value.datetimeEnd) {
    days = new Set(
      eachDayOfInterval({
        start: new Date(formData.value.datetimeStart),
        end: new Date(formData.value.datetimeEnd),
      }).map((date) => mod(getDay(date) - 1, DAYS_OF_WEEK.length)),
    )
  }
  return DAYS_OF_WEEK.map((label, value) => ({
    label,
    value,
    disabled: days && !days.has(value),
  }))
})

/**
 * Available constraint type items based on the selected asset.
 */
const constraintTypeItems = computed(() => {
  let allowedTypes = DEFAULT_CONSTRAINT_TYPES
  if (formData.value.asset) {
    allowedTypes =
      ALLOWED_CONSTRAINT_TYPES_PER_ASSET_TYPE[formData.value.asset.type] ||
      DEFAULT_CONSTRAINT_TYPES
  }
  return Object.entries(allowedTypes).map(([value, { label }]) => ({
    value,
    label,
    disabled: !formData.value.asset,
  }))
})

const validators = {
  asset(value) {
    if (!value) {
      return { valid: false, message: "Selecteer een installatie" }
    }
    return { valid: true }
  },

  datetimeStart(value) {
    if (!value) {
      return { valid: false, message: "Selecteer een startdatum" }
    }
    // Check if start date is at least PROCESSING_BUSINESS_DAYS in the future
    const { date: earliestAllowed, holidays } = addBusinessDays(
      startOfToday(),
      PROCESSING_BUSINESS_DAYS + 1,
    )
    if (new Date(value) < earliestAllowed) {
      // Build error message incl. mention of holidays
      let message = `De startdatum moet minimaal ${PROCESSING_BUSINESS_DAYS} volledige werkdagen in de toekomst liggen`
      if (holidays.length > 0) {
        message += ` (we zijn gesloten op ${formatListString(holidays)})`
      }
      return { valid: false, message }
    }
    return { valid: true }
  },

  datetimeEnd(value) {
    if (!value) {
      return { valid: false, message: "Selecteer een einddatum" }
    }
    // Check if end date is after start date
    if (formData.value.datetimeStart && value <= formData.value.datetimeStart) {
      return {
        valid: false,
        message: "De einddatum moet na de startdatum liggen",
      }
    }
    return { valid: true }
  },

  recurringDays(value) {
    if (formData.value.isRecurring && (!value || value.length === 0)) {
      return { valid: false, message: "Selecteer minimaal één dag" }
    }
    return { valid: true }
  },

  recurringHours() {
    if (formData.value.isRecurring) {
      // Rely on the inner validation method of the time slot input component
      const validate = timeSlotRef.value?.inputRef?.validate
      if (typeof validate !== "function") {
        // Component not ready or validate method not available: assume valid
        return { valid: true }
      }

      const valid = validate()
      if (!valid) {
        return { valid: false, message: "Selecteer achtereenvolgende uren" }
      }
    }
    return { valid: true }
  },

  constraintType(value) {
    if (!value) {
      return { valid: false, message: "Selecteer een type beperking" }
    }
    return { valid: true }
  },

  constraintValue(value) {
    if (!value && value !== 0) {
      return { valid: false, message: "Voer een (geldige) waarde in" }
    }
    // Value must be at least 0
    if (value < 0) {
      return { valid: false, message: "De waarde moet minstens 0 zijn" }
    }
    // Percentage must be less than 100
    const isPercentage =
      formData.value.constraintType === CONSTRAINT_TYPES.MAX_POWER_IN_FACTOR ||
      formData.value.constraintType === CONSTRAINT_TYPES.MAX_POWER_OUT_FACTOR
    if (isPercentage && value >= 100) {
      return { valid: false, message: "De waarde moet kleiner dan 100% zijn" }
    }
    return { valid: true }
  },

  message() {
    if (
      !formData.value.message ||
      formData.value.message.length < MIN_MESSAGE_LENGTH
    ) {
      return {
        valid: false,
        message: `Gebruik minstens ${MIN_MESSAGE_LENGTH} tekens voor een toelichting`,
      }
    }
    return { valid: true }
  },
}

/**
 * Symbol to show in the InputField addon for the constraint value, based on the
 * selected constraint type.
 */
const valueAddon = computed(() => {
  if (!formData.value.asset || !formData.value.constraintType) {
    return ""
  }
  const allowedTypes =
    ALLOWED_CONSTRAINT_TYPES_PER_ASSET_TYPE[formData.value.asset.type] || {}
  const typeInfo = allowedTypes[formData.value.constraintType]
  return typeInfo ? typeInfo.addon : ""
})

/**
 * Resets the constraint type and value.
 */
function resetConstraint() {
  formData.value.constraintType = null
  formData.value.constraintValue = null
}

/**
 * Deselects recurring days that are disabled based on the current start and end
 * dates.
 */
function deselectDisabledRecurringDays() {
  if (!formData.value.datetimeStart || !formData.value.datetimeEnd) {
    return
  }
  recurringDayItems.value
    .filter((item) => item.disabled)
    .forEach((item) => {
      const index = formData.value.recurringDays.indexOf(item.value)
      if (index !== -1) {
        formData.value.recurringDays.splice(index, 1)
      }
    })
}

const currentConstraintSpec = computed(() => {
  return ALLOWED_CONSTRAINT_TYPES_PER_ASSET_TYPE[formData.value.asset?.type]?.[
    formData.value.constraintType
  ]
})

const summary = computed(() => {
  return {
    datetimeStart: formData.value.datetimeStart
      ? formatDateTime(formData.value.datetimeStart)
      : SUMMARY_PLACEHOLDER,
    datetimeEnd: formData.value.datetimeEnd
      ? formatDateTime(formData.value.datetimeEnd)
      : SUMMARY_PLACEHOLDER,
    isRecurring:
      (formData.value.isRecurring &&
        (formData.value.recurringDays.length ||
          formData.value.recurringHours.length)) ||
      "",
    recurringDays: formData.value.isRecurring
      ? formatWeekdaysString(formData.value.recurringDays, true)
      : null,
    recurringHours: formData.value.isRecurring
      ? formatHoursString(formData.value.recurringHours, true)
      : null,
    quantity: currentConstraintSpec.value?.quantity || SUMMARY_PLACEHOLDER,
    value: formData.value.constraintValue || SUMMARY_PLACEHOLDER,
    unit: currentConstraintSpec.value?.unit,
    name: formData.value.asset?.displayName || SUMMARY_PLACEHOLDER,
  }
})

function onError(error) {
  submitting.value = false
  if (error.code === HTTP_STATUS.BAD_REQUEST) {
    formRef.value.validate()
    notificationStore.pushError(
      "Kan formulier niet versturen",
      "Sommige verplichte velden zijn niet ingevuld.",
      "form-invalid",
    )
  } else {
    notificationStore.pushError(
      "Kan formulier niet versturen",
      `Er is een fout opgetreden. Probeer het later opnieuw. (code: ${error.code})`,
      "form-error",
    )
  }
}

function onSubmit() {
  if (!formRef.value.validate(true)) {
    triggerEvent("contact:disruption-form-invalid")
    return
  }
  submitting.value = true

  const data = {
    user_id: useUserStore().id,
    portfolio_id: useFilterStore().currentPortfolio.id,
    metering_point_id: formData.value.asset.id,
    datetime_start: new Date(formData.value.datetimeStart),
    datetime_end: new Date(formData.value.datetimeEnd),
    constraint_type: formData.value.constraintType,
    constraint_value: formData.value.constraintValue,
    message: formData.value.message,
  }
  if (formData.value.isRecurring) {
    data.recurring_days = formData.value.recurringDays
    data.recurring_hours = formData.value.recurringHours
  }
  useContactStore().submitForm(
    FORM_CATEGORY,
    data,
    () => {
      submitting.value = false
      submitted.value = true
      triggerEvent("contact:disruption-form-submitted")
    },
    onError,
  )
}

function resetForm() {
  formData.value = {
    asset: null,
    datetimeStart: null,
    datetimeEnd: null,
    isRecurring: false,
    recurringDays: [],
    recurringHours: [],
    constraintType: null,
    constraintValue: null,
    message: "",
  }
  submitting.value = false
  submitted.value = false
}
</script>
